interface BaseConfig {
  portalClientId: string;
  clientIdsForBanner: string[];
  athenaOneWebClientIds: string[];
  athenaOneMobileClientIds: string[];
  anetUserProfileClientId: string[];
  amplitudeApiKey: string;
  defaultAthenaOneHost: string;
  aOneBaseUrl: string;
  marketingContentUrl?: URL;
  oktaUrl: string;
  env: 'dev' | 'int' | 'preprod' | 'prod';
  lwaSpaClientIds: string[];
  iamCoreUrl: string;
  manageProfileClientSettings: Record<string, Record<string, string>>;
  lwaBaseUri: string;
  shouldBlockPasswordUpdateForProdUser: boolean;
  athenaOneResetPasswordLambdaBaseUrl: string;
  defaultNamespace?: string;
  /**
   * Feature Flags
   */
  featureFlags: {
    /**
     * When enabled, the forgot password link for athenaOne web clients will use Okta's native flow
     * When disabled, the existing custom flow will be used
     */
    enableNativePasswordReset: boolean;
    /**
     * When enabled, reads anet_db from URL params to namespace users
     */
    enableAnetDbNamespace?: boolean;
    /**
     * When enabled, expired password resets are handled via the Lambda endpoint
     * instead of the legacy ESP page in the monolith.
     */
    enableExpiredPasswordResetViaLambda: boolean;
  };
}

interface AuthServerConfig {
  featureFlags: {
    enableNativePasswordReset: boolean;
    enableAnetDbNamespace?: boolean;
    enableExpiredPasswordResetViaLambda: boolean;
  };
  iamCoreUrl: string;
  manageProfileClientSettings: Record<string, Record<string, string>>;
  lwaBaseUri: string;
  shouldBlockPasswordUpdateForProdUser: boolean;
  athenaOneResetPasswordLambdaBaseUrl: string;
}

interface AthenaEnvConfig extends BaseConfig {
  athenaNetPublickKeyUrl: URL;
  athenaNetPasswordResetUrl: string;
  defaultAthenaOneLoginUrl: string;
  athenaOneChangeExpiredPasswordUrl: string;
  athenaOneSymantecPromptUrl: string;
  getSecurityQuestionsTokenUrl: string;
  authServerId: string;
  prodAOneBaseUrl: string;
}

const devAthenaOneOrigin = process.env.REACT_APP_ATHENAONE_ORIGIN || 'https://candidate-regression.athenahealth.com';

const localConfig: BaseConfig = {
  portalClientId: process.env.REACT_APP_PORTAL_CLIENTID || '0oa5are7tf2rxpSAS1t7',
  clientIdsForBanner: process.env.REACT_APP_CLIENTIDS_FOR_BANNER ||
    [
      '0oa5are7tf2rxpSAS1t7', // Communicator Patient Portal
    ],
  featureFlags: {
    enableNativePasswordReset: process.env.REACT_APP_ENABLE_NATIVE_PASSWORD_RESET === 'true',
    enableAnetDbNamespace: process.env.REACT_APP_ENABLE_ANET_DB_NAMESPACE === 'true',
    enableExpiredPasswordResetViaLambda: process.env.REACT_APP_ENABLE_EXPIRED_PASSWORD_RESET_VIA_LAMBDA === 'true',
  },
  athenaOneWebClientIds: process.env.REACT_APP_ATHENAONE_WEB_CLIENTIDS || [
    '0oa554hzbhSbGogvt1t7', // athenaNet
  ],
  athenaOneMobileClientIds: process.env.REACT_APP_ATHENAONE_MOBILE_CLIENTIDS || [
    '0oah2qpbo0AnEFxuW1t7', // athenaOne Mobile - development
    '0oaon0ul3vr8dF1Nn1t7', // PKCE athenaOne Mobile - development
    '0oatgusivxQkk6Hrr1t7', // Test AthenaOne Mobile - development
  ],
  manageProfileClientSettings: {
    '0oa10jmz9nyEM2HoR1t8': {
      manageFactorUrl: process.env.REACT_APP_MANAGE_FACTOR_URL || 'https://lwa-user-profile-management.athenahealth.com:33000/managefactors',
      userProfileUrl: 'https://lwa-user-profile-management.athenahealth.com:33000',
    },
    '0oa24wvh20dXOKUTb0h8': {
      manageFactorUrl: process.env.REACT_APP_MANAGE_FACTOR_URL || 'https://lwa-user-profile-management.athenahealth.com:33000/managefactors',
      userProfileUrl: 'https://lwa-user-profile-management.athenahealth.com:33000',
    },
    '0oaw5ijqpbB1AkEHn297': {
      manageFactorUrl: process.env.REACT_APP_MANAGE_FACTOR_URL || 'https://lwa-user-profile-management.athenahealth.com:33000/managefactors',
      userProfileUrl: 'https://lwa-user-profile-management.athenahealth.com:33000',
    },
    default: {
      manageFactorUrl: 'https://lwa-user-profile-management.athenahealth.com:33000/managefactors',
      userProfileUrl: 'https://lwa-user-profile-management.athenahealth.com:33000',
    },
  },
  anetUserProfileClientId: [ '0oaqo4y5tiLrVisdo1t7' ],
  amplitudeApiKey: '20dc2a80bf07774d9fc999f614e9ea5e',
  aOneBaseUrl: devAthenaOneOrigin,
  marketingContentUrl: new URL('https://anet.aws.caas.athenahealth.com/'),
  env: process.env.REACT_APP_ENV || 'dev',
  oktaUrl: process.env.REACT_APP_BANNER_JWT_AUD || 'https://identity.dev.athenahealth.com',
  defaultAthenaOneHost: devAthenaOneOrigin.split('https://')[1],
  lwaSpaClientIds: process.env.REACT_APP_LWA_SPA_CLIENT_IDS || [ '0oa15p00avlpi6ji61t8', '0oa15p003qpXt5lPv1t8' ],
  iamCoreUrl: process.env.REACT_APP_IAM_CORE_SERVER_URL || 'https://iam-core.uiam:20020/',
  lwaBaseUri: process.env.REACT_APP_LWA_BASE_URI || 'https://login.dev.platform.athenahealth.com:3000/',
  shouldBlockPasswordUpdateForProdUser: false,
  athenaOneResetPasswordLambdaBaseUrl: process.env.REACT_APP_ATHENAONE_RESET_PASSWORD_LAMBDA_BASE_URL || 'https://q2j86jzyhe.execute-api.us-east-1.amazonaws.com/dev/athenaone-reset-password',
};

const devConfig: BaseConfig = {
  portalClientId: '0oa5are7tf2rxpSAS1t7',
  clientIdsForBanner: [
    '0oa5are7tf2rxpSAS1t7', // Communicator Patient Portal
  ],
  featureFlags: {
    enableNativePasswordReset: true,
    enableAnetDbNamespace: false,
    enableExpiredPasswordResetViaLambda: true,
  },
  defaultNamespace: 'candreg',
  athenaOneWebClientIds: [
    '0oa554hzbhSbGogvt1t7', // athenaNet
  ],
  athenaOneMobileClientIds: [
    '0oah2qpbo0AnEFxuW1t7', // athenaOne Mobile - development
    '0oaon0ul3vr8dF1Nn1t7', // PKCE athenaOne Mobile - development
    '0oatgusivxQkk6Hrr1t7', // Test AthenaOne Mobile - development
    '0oaus43bkaNKUaNGc1t7', // athenaOne android app - development
  ],
  manageProfileClientSettings: {
    '0oa10jmz9nyEM2HoR1t8': {
      manageFactorUrl: process.env.REACT_APP_MANAGE_FACTOR_URL || 'https://myidentity-dev-profile.platform.athenahealth.com/managefactors',
      userProfileUrl: 'https://myidentity-dev-profile.platform.athenahealth.com',
    },
    default: {
      manageFactorUrl: 'https://myidentity-dev-profile.platform.athenahealth.com/managefactors',
      userProfileUrl: 'https://myidentity-dev-profile.platform.athenahealth.com',
    },
  },
  anetUserProfileClientId: [ '0oaqo4y5tiLrVisdo1t7' ],
  amplitudeApiKey: '20dc2a80bf07774d9fc999f614e9ea5e',
  aOneBaseUrl: 'https://candidate-regression.athenahealth.com',
  marketingContentUrl: new URL('https://anetdev.aws.caas.athenahealth.com'),
  env: 'dev',
  oktaUrl: 'https://identity.dev.athenahealth.com',
  defaultAthenaOneHost: 'candidate-regression.athenahealth.com',
  lwaSpaClientIds: process.env.REACT_APP_LWA_SPA_CLIENT_IDS || [ '0oa15p00avlpi6ji61t8', '0oa15p003qpXt5lPv1t8' ],
  iamCoreUrl: process.env.REACT_APP_IAM_CORE_SERVER_URL || 'https://identity.dev.athena.io/identity/',
  lwaBaseUri: process.env.REACT_APP_LWA_BASE_URI || 'https://myidentity-dev.aws.athenahealth.com/',
  shouldBlockPasswordUpdateForProdUser: false,
  athenaOneResetPasswordLambdaBaseUrl: 'https://q2j86jzyhe.execute-api.us-east-1.amazonaws.com/dev/athenaone-reset-password',
};

const intConfig: BaseConfig = {
  portalClientId: '0oaduemiyvRIywGST0h7',
  clientIdsForBanner: [
    '0oaduemiyvRIywGST0h7', // Communicator Patient Portal
  ],
  featureFlags: {
    enableNativePasswordReset: true,
    enableAnetDbNamespace: false,
    enableExpiredPasswordResetViaLambda: true,
  },
  defaultNamespace: 'devma',
  athenaOneWebClientIds: [
    '0oac0y9mixYkeU8oH0h7', // athenaNet
  ],
  athenaOneMobileClientIds: [
    '0oawn8pq59Qt3XLTY0h7', // athenaOne Mobile testing - integration
    '0oa1b2ojxadvDorFh0h8', // PKCE athenaOne Mobile - integration
    '0oa1pgg7ke8hKqSbU0h8', // Test AthenaOne Mobile - integration
    '0oa1tkq2r6xkA6NMY0h8', // athenaOne android app - integration
  ],
  manageProfileClientSettings: {
    '0oa24wvh20dXOKUTb0h8': {
      manageFactorUrl: 'https://myidentity-int-profile.platform.athenahealth.com/managefactors',
      userProfileUrl: 'https://myidentity-int-profile.platform.athenahealth.com',
    },
    default: {
      manageFactorUrl: 'https://myidentity-int-profile.platform.athenahealth.com/managefactors',
      userProfileUrl: 'https://myidentity-int-profile.platform.athenahealth.com',
    },
  },
  anetUserProfileClientId: [ '0oa1hbf8sfpXu9LkB0h8' ],
  amplitudeApiKey: '20dc2a80bf07774d9fc999f614e9ea5e',
  aOneBaseUrl: 'https://devmain-masked.athenahealth.com',
  marketingContentUrl: new URL('https://anetdev.aws.caas.athenahealth.com'),
  env: 'int',
  oktaUrl: 'https://identity.int.athenahealth.com',
  defaultAthenaOneHost: 'devmain-masked.athenahealth.com',
  lwaSpaClientIds: [ '0oa2czeng5zJZ53Fw0h8', '0oa2czeigpbVuXgzg0h8' ],
  iamCoreUrl: process.env.REACT_APP_IAM_CORE_SERVER_URL || 'https://identity.int.athena.io/identity/',
  lwaBaseUri: process.env.REACT_APP_LWA_BASE_URI || 'https://myidentity-int.aws.athenahealth.com/',
  shouldBlockPasswordUpdateForProdUser: false,
  athenaOneResetPasswordLambdaBaseUrl: 'https://5pyjqqzupl.execute-api.us-east-1.amazonaws.com/int/athenaone-reset-password',
};

const preprodConfig: BaseConfig = {
  portalClientId: '0oaxi07eiyhqIY17k297',
  clientIdsForBanner: [
    '0oaxi07eiyhqIY17k297',   // Communicator Patient Portal
  ],
  featureFlags: {
    enableNativePasswordReset: true,
    enableAnetDbNamespace: false,
    enableExpiredPasswordResetViaLambda: true,
  },
  defaultNamespace: 'candstg',
  athenaOneWebClientIds: [
    '0oaxb4e21s48Yg87x297', // athenaNet
  ],
  athenaOneMobileClientIds: [],
  manageProfileClientSettings: {
    '0oaw5ijqpbB1AkEHn297': {
      manageFactorUrl: 'https://myidentity-preprod-profile.platform.athenahealth.com/managefactors',
      userProfileUrl: 'https://myidentity-preprod-profile.platform.athenahealth.com',
    },
    default: {
      manageFactorUrl: 'https://myidentity-preprod-profile.platform.athenahealth.com/managefactors',
      userProfileUrl: 'https://myidentity-preprod-profile.platform.athenahealth.com',
    },
  },
  anetUserProfileClientId: [ '0oaxkj5fme1YFaLjH297' ],
  amplitudeApiKey: 'e83bc020b299658e1e1c454fc9616d92',
  aOneBaseUrl: 'https://candidate-stage.athenahealth.com',
  marketingContentUrl: new URL('https://anetdev.aws.caas.athenahealth.com'),
  env: 'preprod',
  oktaUrl: 'https://identity.preprod.athenahealth.com',
  defaultAthenaOneHost: 'candidate-stage.athenahealth.com',
  lwaSpaClientIds: [ '0oaxgtua2rwQg9HJA297', '0oaxgqt14a0LXElo1297' ],
  iamCoreUrl: process.env.REACT_APP_IAM_CORE_SERVER_URL || 'https://identity.preprod.athena.io/identity/',
  lwaBaseUri: process.env.REACT_APP_LWA_BASE_URI || 'https://myidentity-preprod.aws.athenahealth.com/',
  shouldBlockPasswordUpdateForProdUser: false,
  athenaOneResetPasswordLambdaBaseUrl: 'https://7hvhyxuvmi.execute-api.us-east-1.amazonaws.com/preprod/athenaone-reset-password',
};

const prodConfig: BaseConfig = {
  portalClientId: '0oaku1tngsTH20pA1296',
  clientIdsForBanner: [
    '0oaku1tngsTH20pA1296', // Communicator Patient Portal
  ],
  featureFlags: {
    enableNativePasswordReset: true,
    enableAnetDbNamespace: false,
    enableExpiredPasswordResetViaLambda: true,
  },
  athenaOneWebClientIds: [
    '0oaet0rfjNzyKCiQQ296', // athenaNet
    '0oa4zsgxk7LR1gMAo297', // athenaNet - ClientSandbox
    '0oa1g3qnz9iqZWIjR297', // athenaNet - ClientTrain
    '0oa16evw7trKLV80X297', // athenaNet - Preview
    '0oa1g3pp3dNEa1Rhm297', // athenaNet - salesdemo
    '0oa1g3qt4lLFlpa7d297', // athenaNet - Sandbox
  ],
  athenaOneMobileClientIds: [
    '0oa6ul7lt1uvcNBz8297', // athenaOne Mobile
    '0oa6ulmxvpXJ0IUVy297', // athenaOne Mobile - preview
    '0oa6uljb2e7eIila7297', // athenaOne Mobile - salesdemo
    '0oae3x8ssiw1KDxLz297', // PKCE athenaOne Mobile
    '0oakwjdee7ew9gP8w297', // athenaOne android app
    '0oakzzyg6iiiaEcpp297', // athenaOne android app - salesdemo
    '0oal0019ls7P9RTv6297', // athenaOne android app - preview
    '0oal0066lwYUCKz5w297', // athenaOne android app - sandbox
    '0oal0046kfGYYH85U297', // athenaOne android app - clienttrain
  ],
  manageProfileClientSettings: {
    '0oar6go3edRUglvkD297': {
      manageFactorUrl: 'https://myidentity-profile.platform.athenahealth.com/managefactors',
      userProfileUrl: 'https://myidentity-profile.platform.athenahealth.com',
    },
    '0oarf18q39wbA4VhK297': {
      manageFactorUrl: 'https://myidentity-support-profile.platform.athenahealth.com/managefactors',
      userProfileUrl: 'https://myidentity-support-profile.platform.athenahealth.com',
    },
    '0oarf1cw47OHBe4pG297': {
      manageFactorUrl: 'https://myidentity-support-demo-profile.platform.athenahealth.com/managefactors',
      userProfileUrl: 'https://myidentity-support-demo-profile.platform.athenahealth.com',
    },
    default: {
      manageFactorUrl: 'https://myidentity-profile.platform.athenahealth.com/managefactors',
      userProfileUrl: 'https://myidentity-profile.platform.athenahealth.com',
    },
  },
  anetUserProfileClientId: [
    '0oag8khx95iPKkPcK297', // prod
    '0oai4q0mtkYSkqgNX297', // suport envs
  ],
  amplitudeApiKey: 'c41bf44737b4598dcae4c0eaec9b5833',
  aOneBaseUrl: 'https://athenanet.athenahealth.com',
  marketingContentUrl: new URL('https://anet.aws.caas.athenahealth.com?v=1'),
  env: 'prod',
  oktaUrl: 'https://identity.athenahealth.com',
  defaultAthenaOneHost: 'athenanet.athenahealth.com',
  lwaSpaClientIds: [ '0oawxcmh2iNaGSIOt297', '0oawxcn46kyxQuSSS297', '0oawxcngbjquascDI297' ],
  iamCoreUrl: 'https://identity.athena.io/identity/',
  lwaBaseUri: 'https://myidentity.platform.athenahealth.com/',
  shouldBlockPasswordUpdateForProdUser: false,
  athenaOneResetPasswordLambdaBaseUrl: 'https://7ho8ly4ny0.execute-api.us-east-1.amazonaws.com/prod/athenaone-reset-password',
};

const supportPhiConfig: AuthServerConfig = {
  featureFlags: {
    enableNativePasswordReset: true,
    enableAnetDbNamespace: false,
    enableExpiredPasswordResetViaLambda: true,
  },
  iamCoreUrl: 'https://identity.preview.api.athena.io/identity/',
  manageProfileClientSettings: {
    default: {
      manageFactorUrl: 'https://myidentity-support-profile.platform.athenahealth.com/managefactors',
      userProfileUrl: 'https://myidentity-support-profile.platform.athenahealth.com',
    },
  },
  lwaBaseUri: 'https://myidentity-support.platform.athenahealth.com/',
  shouldBlockPasswordUpdateForProdUser: true,
  athenaOneResetPasswordLambdaBaseUrl: 'https://xlygjgq2nk.execute-api.us-east-1.amazonaws.com/preview/athenaone-reset-password',
};

const supportNonPhiConfig: AuthServerConfig = {
  featureFlags: {
    enableNativePasswordReset: true,
    enableAnetDbNamespace: false,
    enableExpiredPasswordResetViaLambda: true,
  },
  iamCoreUrl: 'https://identity.salesdemo.api.athena.io/identity/',
  manageProfileClientSettings: {
    default: {
      manageFactorUrl: 'https://myidentity-support-demo-profile.platform.athenahealth.com/managefactors',
      userProfileUrl: 'https://myidentity-support-demo-profile.platform.athenahealth.com',
    },
  },
  lwaBaseUri: 'https://myidentity-support-demo.platform.athenahealth.com/',
  shouldBlockPasswordUpdateForProdUser: true,
  athenaOneResetPasswordLambdaBaseUrl: 'https://3qgc5hj63b.execute-api.us-east-1.amazonaws.com/salesdemo/athenaone-reset-password',
};

export const envConfigMappings: Record<string, BaseConfig> = {
  'http://localhost:50057': localConfig,
  'http://identity.test.athenahealth.com:50057': localConfig,
  'https://identity.dev.athenahealth.com': devConfig,
  'https://identity.int.athenahealth.com': intConfig,
  'https://identity.athenahealth.com': prodConfig,
  'https://identity.preprod.athenahealth.com': preprodConfig,
};

const authServerEnvConfigMapping: Record<string, AuthServerConfig> = {
  ausu5cwvrZnWYasjS296: supportPhiConfig,
  aus2hfei6ookPyyCA297: supportPhiConfig,
  aus1eumlr5MD3rYcn297: supportNonPhiConfig,
};

const formUrlWithOriginAndPath = (origin: string, path: string) => {
  return new URL(path, origin).href;
};

const getAuthServerId = () => {
  const requestContext = global.OktaUtil?.getRequestContext();
  if (requestContext) {
    return requestContext?.authentication?.issuer?.id;
  }
};

const getAuthServerConfig = () => {
  const authServerId = getAuthServerId();
  if (authServerId) {
    return authServerEnvConfigMapping[authServerId] || {};
  }
  return {};
};

export function makeFinalConfig(config: BaseConfig): AthenaEnvConfig {
  const authServerConfig = getAuthServerConfig();
  config = {
    ...config,
    ...authServerConfig,
  };
  return {
    ...config,
    // TO DO: cleanup & add new domain after https://athenajira.athenahealth.com/browse/NOC-42910 is done
    athenaNetPublickKeyUrl: new URL('https://identity-static-assets.platform.athenahealth.com/athenanet-signed-jwt-conf.json'),
    athenaNetPasswordResetUrl: formUrlWithOriginAndPath(config.aOneBaseUrl, '/1/1/resetpassword.esp'),
    defaultAthenaOneLoginUrl: formUrlWithOriginAndPath(config.aOneBaseUrl, '/login.esp'),
    athenaOneChangeExpiredPasswordUrl: formUrlWithOriginAndPath(config.aOneBaseUrl, '/1/1/login/changeoktaoieexpiredpassword.esp'),
    athenaOneSymantecPromptUrl: formUrlWithOriginAndPath(config.aOneBaseUrl, '/1/1/login/oktasymantecvipprompt.esp'),
    getSecurityQuestionsTokenUrl: formUrlWithOriginAndPath(config.aOneBaseUrl, '/1/1/login/getsqtoken.esp'),
    prodAOneBaseUrl: 'https://athenanet.athenahealth.com',
    authServerId: getAuthServerId(),
  };
}

// Helps IDE to do auto-import
const athenaConfig = makeFinalConfig(
  process.env.REACT_APP_TEST_DEV_PUPP ? localConfig : envConfigMappings[window.location.origin] || devConfig
);

export default athenaConfig;
